/**
 * UI Interactions bootstrap
 *
 * This module initializes common client-side UI behaviors used across the application:
 * - Sets up DataTables with project-specific defaults
 * - Toggles the main sidebar visibility
 * - Handles dropdown toggles with arrow state management
 * - Opens authentication modals (login/register) based on data attributes
 * - Enables horizontal scrolling for top navigation category blocks and updates arrow visibility
 *
 * The code is intended to be executed once on page load.
 */

import { $ } from '../../base/bootstrap';
import '../../base/base';

import { setupDataTable } from 'utils/custom-helpers';

/**
 * Initializes all UI behaviors for the page.
 */
function initializeUIInteractions(): void {
    setupDataTable();
    bindCasinoIframeRedirect();
    bindSidebarToggle();
    bindDropdownToggles();
    bindHeaderAccountDropdown();
    bindAuthModalLinks();
    bindAutoMarquee();
    bindTopNavigationScroller();
}

function onDomReady(callback: () => void): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback, { once: true });
        return;
    }

    callback();
}

/**
 * Preserves the casino iframe redirect behavior for default casino layouts.
 */
function bindCasinoIframeRedirect(): void {
    window.addEventListener('load', () => {
        if (!window.IS_CASINO) {
            return;
        }

        const app = document.getElementById('app');
        if (!app) {
            return;
        }

        window.IS_USER_IN_PLAY = app.dataset.isUserInPlay === 'true';

        let parentLocation: Location | null;
        try {
            parentLocation = window.parent.location;
        } catch (error) {
            parentLocation = null;
        }

        if (window.location === parentLocation) {
            return;
        }

        const casinoHomeUrl = app.dataset.casinoHomeUrl || window.CASINO_URL;
        if (casinoHomeUrl) {
            window.top.location.href = casinoHomeUrl;
        }

        document.body = document.createElement('body');
        document.body.innerHTML = '';
        window.stop();
    });
}

function closeSidebar(): void {
    $('#app').removeClass('sidebar-open');
    $('body').removeClass('sidebar-lock');
    closeSidebarDropdowns(false);
}

function openSidebar(): void {
    $('#app').addClass('sidebar-open');

    if (window.innerWidth <= 1024) {
        $('body').addClass('sidebar-lock');
    }
}

function setSidebarState(isOpen: boolean): void {
    if (isOpen) {
        openSidebar();
        return;
    }

    closeSidebar();
}

function closeSidebarDropdowns(animate: boolean): void {
    const wrappers = document.querySelectorAll<HTMLElement>('.sidebar .dropdown-wrapper');

    wrappers.forEach((wrapper) => {
        applySidebarDropdownState(wrapper, false, animate);
    });
}

/**
 * Binds click handlers and global events for showing/hiding the sidebar.
 * Includes:
 * - open/close toggle buttons
 * - backdrop click to close
 * - ESC to close
 * - prevent sidebar clicks from bubbling to backdrop/document handlers
 */
function bindSidebarToggle(): void {
    $(document).on('click', '#show-sidebar, #toggle-sidebar, .mobile-hamburger-button', function (e) {
        e.preventDefault();
        const isOpen = !$('#app').hasClass('sidebar-open');
        setSidebarState(isOpen);
    });

    $(document).on('click', '#sidebar-overlay', function () {
        closeSidebar();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeSidebar();
        }
    });
}

/**
 * Binds click handlers for custom dropdown buttons,
 * including visual state (active class and arrow direction).
 */
function bindDropdownToggles(): void {
    onDomReady(() => {
        const buttons = document.querySelectorAll<HTMLButtonElement>('.js-toggle-dropdown');

        buttons.forEach((button) => {
            const sidebarWrapper = button.closest<HTMLElement>('.sidebar .dropdown-wrapper');
            if (sidebarWrapper) {
                applySidebarDropdownState(sidebarWrapper, button.classList.contains('active'), false);
                button.addEventListener('click', (event) => {
                    event.preventDefault();
                    const isActive = !sidebarWrapper.classList.contains('is-open');
                    applySidebarDropdownState(sidebarWrapper, isActive, true);
                });
                return;
            }

            syncDropdownState(button, button.classList.contains('active'));

            button.addEventListener('click', () => {
                const isActive = button.classList.toggle('active');
                syncDropdownState(button, isActive);
            });
        });
    });
}

function applySidebarDropdownState(wrapper: HTMLElement, isOpen: boolean, animate: boolean): void {
    const content = wrapper.querySelector<HTMLElement>('.dropdown-content');
    const button = wrapper.querySelector<HTMLButtonElement>('.js-toggle-dropdown');
    const arrowIcon = button?.querySelector<HTMLElement>('.arrow i') ?? null;

    wrapper.classList.toggle('is-open', isOpen);
    button?.classList.toggle('active', isOpen);
    content?.classList.toggle('show', isOpen);

    if (arrowIcon) {
        arrowIcon.classList.toggle('fa-chevron-up', isOpen);
        arrowIcon.classList.toggle('fa-chevron-down', !isOpen);
    }

    if (!content) {
        return;
    }

    if (!animate) {
        $(content).stop(true, true)[isOpen ? 'show' : 'hide']();
        return;
    }

    $(content).stop(true, true)[isOpen ? 'slideDown' : 'slideUp'](isOpen ? 200 : 180);
}

function syncDropdownState(button: HTMLButtonElement, isActive: boolean): void {
    const dropdown = button.nextElementSibling as HTMLElement | null;
    if (!dropdown) return;

    dropdown.classList.toggle('show', isActive);

    const arrowIcon = button.querySelector('.arrow i');
    if (arrowIcon) {
        arrowIcon.classList.toggle('fa-chevron-up', isActive);
        arrowIcon.classList.toggle('fa-chevron-down', !isActive);
    }
}

function bindHeaderAccountDropdown(): void {
    onDomReady(() => {
        const accountDropdowns = Array.from(document.querySelectorAll<HTMLElement>('.account-dropdown-menu'));

        if (!accountDropdowns.length) {
            return;
        }

        const isMobileViewport = () => window.innerWidth <= 1024;

        const closeDropdown = (dropdown: HTMLElement): void => {
            dropdown.classList.remove('is-open');

            const trigger = dropdown.querySelector<HTMLElement>('.account-profile-trigger');

            trigger?.setAttribute('aria-expanded', 'false');
        };

        const closeAllDropdowns = (): void => {
            accountDropdowns.forEach(closeDropdown);
        };

        accountDropdowns.forEach((dropdown) => {
            const trigger = dropdown.querySelector<HTMLButtonElement>('.account-profile-trigger');
            const menu = dropdown.querySelector<HTMLElement>('.menu-account');

            if (!trigger || !menu) {
                return;
            }

            trigger.addEventListener('click', (event) => {
                if (!isMobileViewport()) {
                    return;
                }

                event.preventDefault();
                event.stopPropagation();

                const willOpen = !dropdown.classList.contains('is-open');

                closeAllDropdowns();

                if (!willOpen) {
                    return;
                }

                dropdown.classList.add('is-open');
                trigger.setAttribute('aria-expanded', 'true');
            });

            menu.addEventListener('click', (event) => {
                event.stopPropagation();
            });
        });

        document.addEventListener('click', (event) => {
            const target = event.target;

            if (!(target instanceof Node)) {
                return;
            }

            const clickedInsideDropdown = accountDropdowns.some((dropdown) => dropdown.contains(target));

            if (!clickedInsideDropdown) {
                closeAllDropdowns();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                closeAllDropdowns();
            }
        });

        window.addEventListener('resize', () => {
            if (!isMobileViewport()) {
                closeAllDropdowns();
            }
        });
    });
}
/**
 * Binds delegated click handling for links that should open
 * authentication modals (login or register).
 */
function bindAuthModalLinks(): void {
    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const link = event.target.closest('[data-open-auth-modal]');
        if (!link) return;

        const modeAttr = link.getAttribute('data-open-auth-modal');
        const mode = modeAttr === 'login' || modeAttr === 'register' ? modeAttr : null;
        if (!mode) return;

        if (window.openAuthModal) {
            event.preventDefault();
            window.openAuthModal(mode);
        }
    });
}

/**
 * Creates a seamless looping marquee for the latest wins strip.
 * Rebuilds clones after realtime snapshot updates replace the track contents.
 */
function bindAutoMarquee(): void {
    onDomReady(() => {
        const tracks = document.querySelectorAll<HTMLElement>('.auto-marquee');

        tracks.forEach((track) => {
            let observer: MutationObserver | null = null;
            let resizeTimeout: number | null = null;
            let isSyncing = false;

            const getOriginalItems = (): HTMLElement[] => {
                return Array.from(track.children).filter((child): child is HTMLElement => {
                    return child instanceof HTMLElement && child.dataset.marqueeClone !== 'true';
                });
            };

            const getSequenceWidth = (items: HTMLElement[], gap: number): number => {
                return items.reduce((width, item) => {
                    return width + item.getBoundingClientRect().width + gap;
                }, 0);
            };

            const syncTrack = (): void => {
                if (isSyncing) {
                    return;
                }

                isSyncing = true;
                observer?.disconnect();

                track.querySelectorAll<HTMLElement>('[data-marquee-clone="true"]').forEach((clone) => clone.remove());

                const originals = getOriginalItems();
                if (originals.length === 0) {
                    track.classList.remove('is-auto-marquee-ready');
                    track.style.removeProperty('--shift');
                    track.style.removeProperty('--duration');
                    observer?.observe(track, { childList: true });
                    isSyncing = false;
                    return;
                }

                const style = getComputedStyle(track);
                const gapValue = style.columnGap || style.gap || '0';
                const gap = parseFloat(gapValue) || 0;

                const sequenceWidth = getSequenceWidth(originals, gap);

                const viewportWidth = track.parentElement?.getBoundingClientRect().width ?? 0;
                if (sequenceWidth <= 0 || viewportWidth <= 0) {
                    track.classList.remove('is-auto-marquee-ready');
                    track.style.removeProperty('--shift');
                    track.style.removeProperty('--duration');
                    observer?.observe(track, { childList: true });
                    isSyncing = false;
                    return;
                }

                const shouldAnimate = originals.length > 1 || sequenceWidth > viewportWidth;

                if (!shouldAnimate) {
                    track.classList.remove('is-auto-marquee-ready');
                    track.style.removeProperty('--shift');
                    track.style.removeProperty('--duration');
                    observer?.observe(track, { childList: true });
                    isSyncing = false;
                    return;
                }

                let renderedWidth = sequenceWidth;

                while (renderedWidth < viewportWidth + sequenceWidth) {
                    originals.forEach((item) => {
                        const clone = item.cloneNode(true) as HTMLElement;
                        clone.dataset.marqueeClone = 'true';
                        clone.setAttribute('aria-hidden', 'true');
                        track.appendChild(clone);
                    });

                    renderedWidth += sequenceWidth;
                }

                track.style.setProperty('--shift', `${Math.ceil(sequenceWidth)}px`);

                const pxPerSec = 80;
                const duration = Math.max(10, sequenceWidth / pxPerSec);
                track.style.setProperty('--duration', `${duration}s`);
                track.classList.add('is-auto-marquee-ready');

                observer?.observe(track, { childList: true });
                isSyncing = false;
            };

            observer = new MutationObserver(() => {
                syncTrack();
            });

            syncTrack();

            window.addEventListener('resize', () => {
                if (resizeTimeout !== null) {
                    window.clearTimeout(resizeTimeout);
                }

                resizeTimeout = window.setTimeout(() => {
                    syncTrack();
                }, 100);
            });
        });
    });
}

/**
 * Enables horizontal scrolling for top navigation category blocks.
 * Uses left/right arrows for smooth scrolling and hides arrows when
 * the scroller is at the beginning or end.
 */
function bindTopNavigationScroller(): void {
    document.addEventListener('DOMContentLoaded', () => {
        const containers = document.querySelectorAll<HTMLElement>('.top-navigation-blocks-container');

        containers.forEach((container) => {
            const scroller = container.querySelector<HTMLElement>('.category-blocks');
            const arrowLeft = container.querySelector<HTMLElement>('.top-nav-arrow-left');
            const arrowRight = container.querySelector<HTMLElement>('.top-nav-arrow-right');
            const desktopChevronLeft = container.querySelector<HTMLElement>('.category-tabs-wrapper__chevron--left');
            const desktopChevronRight = container.querySelector<HTMLElement>('.category-tabs-wrapper__chevron:not(.category-tabs-wrapper__chevron--left)');
            const desktopFadeLeft = container.querySelector<HTMLElement>('.category-tabs-wrapper__fade--left');
            const desktopFadeRight = container.querySelector<HTMLElement>('.category-tabs-wrapper__fade:not(.category-tabs-wrapper__fade--left)');

            if (!scroller) {
                return;
            }

            function updateArrows(): void {
                const visibleWidth = scroller.clientWidth;
                const maxScroll = Math.max(0, scroller.scrollWidth - visibleWidth);
                const endTolerance = Math.max(6, Math.ceil(visibleWidth * 0.02));
                const canScroll = maxScroll > endTolerance;
                const atStart = scroller.scrollLeft <= endTolerance;
                const remainingScroll = Math.max(0, maxScroll - scroller.scrollLeft);
                const atEnd = remainingScroll <= endTolerance;

                container.classList.toggle('has-scrollable-categories', canScroll);
                arrowLeft?.classList.toggle('hidden', atStart || !canScroll);
                arrowRight?.classList.toggle('hidden', atEnd || !canScroll);

                desktopChevronLeft?.classList.toggle('hidden', atStart || !canScroll);
                desktopChevronRight?.classList.toggle('hidden', atEnd || !canScroll);

                if (desktopFadeLeft) {
                    if (!canScroll || atStart) {
                        desktopFadeLeft.classList.add('is-hidden');
                        desktopFadeLeft.style.opacity = '0';
                    } else {
                        desktopFadeLeft.classList.remove('is-hidden');
                        desktopFadeLeft.style.opacity = String(
                            Math.max(0.35, Math.min(1, 0.55 + (scroller.scrollLeft / Math.max(maxScroll, 1)) * 0.45)),
                        );
                    }
                }

                if (desktopFadeRight) {
                    if (!canScroll || atEnd) {
                        desktopFadeRight.classList.add('is-hidden');
                        desktopFadeRight.style.opacity = '0';
                        return;
                    }

                    desktopFadeRight.classList.remove('is-hidden');
                    desktopFadeRight.style.opacity = String(
                        Math.max(0.35, Math.min(1, 1 - (scroller.scrollLeft / Math.max(maxScroll, 1)) * 0.45)),
                    );
                }
            }

            const getCategoryBlocks = () => Array.from(scroller.querySelectorAll<HTMLElement>('.category-block'));

            const getVisibleBlockIndexes = (blocks: HTMLElement[]) => {
                const viewportStart = scroller.scrollLeft;
                const viewportEnd = viewportStart + scroller.clientWidth;

                const firstVisibleIndex = blocks.findIndex((block) => {
                    const blockEnd = block.offsetLeft + block.offsetWidth;
                    return blockEnd > viewportStart + 4;
                });

                if (firstVisibleIndex === -1) {
                    return {
                        firstVisibleIndex: 0,
                        visibleCount: 1,
                    };
                }

                const lastVisibleIndex = Math.max(
                    firstVisibleIndex,
                    blocks.findLastIndex((block) => block.offsetLeft < viewportEnd - 4),
                );

                return {
                    firstVisibleIndex,
                    visibleCount: Math.max(1, lastVisibleIndex - firstVisibleIndex + 1),
                };
            };

            const scrollToNextCategory = () => {
                const blocks = getCategoryBlocks();
                const { firstVisibleIndex, visibleCount } = getVisibleBlockIndexes(blocks);
                const targetIndex = Math.min(blocks.length - 1, firstVisibleIndex + visibleCount);
                const target = blocks[targetIndex];

                if (!target) {
                    scroller.scrollTo({
                        left: scroller.scrollWidth,
                        behavior: 'smooth',
                    });
                    return;
                }

                scroller.scrollTo({
                    left: Math.max(0, target.offsetLeft - 6),
                    behavior: 'smooth',
                });
            };

            const scrollToPreviousCategory = () => {
                const blocks = getCategoryBlocks();
                const { firstVisibleIndex, visibleCount } = getVisibleBlockIndexes(blocks);
                const targetIndex = Math.max(0, firstVisibleIndex - visibleCount);
                const target = blocks[targetIndex];

                scroller.scrollTo({
                    left: Math.max(0, (target?.offsetLeft ?? 0) - 6),
                    behavior: 'smooth',
                });
            };

            arrowLeft?.addEventListener('click', () => {
                scrollToPreviousCategory();
            });

            arrowRight?.addEventListener('click', () => {
                scrollToNextCategory();
            });

            const isTouchDevice = window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;

            if (desktopChevronRight) {
                const handleNextChevronActivate = (event: Event): void => {
                    event.preventDefault();
                    scrollToNextCategory();
                };

                if (isTouchDevice) {
                    desktopChevronRight.addEventListener('touchend', handleNextChevronActivate, { passive: false });
                }

                desktopChevronRight.addEventListener('click', handleNextChevronActivate);
            }

            if (desktopChevronLeft) {
                const handlePreviousChevronActivate = (event: Event): void => {
                    event.preventDefault();
                    scrollToPreviousCategory();
                };

                if (isTouchDevice) {
                    desktopChevronLeft.addEventListener('touchend', handlePreviousChevronActivate, { passive: false });
                }

                desktopChevronLeft.addEventListener('click', handlePreviousChevronActivate);
            }

            desktopFadeRight?.addEventListener('click', (event) => {
                event.preventDefault();
                scrollToNextCategory();
            });

            desktopFadeLeft?.addEventListener('click', (event) => {
                event.preventDefault();
                scrollToPreviousCategory();
            });

            scroller.addEventListener('scroll', updateArrows);
            window.addEventListener('resize', updateArrows);

            updateArrows();
        });
    });
}

initializeUIInteractions();
